<script lang="ts">
	import CategoryProducts from '$lib/components/category/CategoryProducts.svelte'
	import Filters from '$lib/components/category/Filters.svelte'
	import RecommendedProds from './search/RecommendedProds.svelte'
	import Searchbar from '$lib/modals/search/Searchbar.svelte'
	import LoadingIcon from '$lib/components/account/LoadingIcon.svelte'
	import IntersectionObserver from 'svelte-intersection-observer'
	import { is_set_array, is_set_object } from '$lib/js/helpers'
	import { getMoreSearchProducts } from '$lib/js/products'
	import { beforeNavigate } from '$app/navigation'
	import {
		storeActiveFilters,
		storeSearchedText,
		storeSearchFilters,
		storeSearchModal,
	} from '$lib/js/stores'
	import { page } from '$app/state'
	import { onDestroy, onMount } from 'svelte'
	import type { BaseProduct, FullProduct, ProductQueryOptions } from '$lib/js/elasticsearch/types'
	import { executeSearch } from '$lib/js/search'
	import { getContextScope } from '$lib/js/helpers/i18n'
  import { sendWT3 } from '$lib/js/helpers/wt3'
	import { tick } from 'svelte'

	let start: number = $state(0)
	let products: (BaseProduct | FullProduct)[] = $state([])
	let loadingProds: boolean = $state(false)

	let allResults = $state(false) /* Get results page number */
	let elementOnce: HTMLElement | undefined = $state() /* Infinite scroll */
	let productsDiv: HTMLElement | undefined = $state()

	const scope = getContextScope(),
		endpoint = 'search'

	let options: ProductQueryOptions & { search_text?: string } = $state({
		start: start,
		sort: 'default',
		min_price: 0,
		max_price: 200000,
		search_text: $storeSearchedText,
		brand_ids: [],
		cat_ids: [],
		cat_id: undefined,
	})

	// Save previous filters
	if (is_set_object($storeActiveFilters)) {
		$storeSearchFilters.prev_filters = $storeActiveFilters
	}

	// Clean filters for search
	$storeActiveFilters = {}

	// Handle back actions using popstate
	function handlePopState() {
		$storeSearchModal = false
	}

	onMount(() => {
		// Add a state entry to allow back actions to be detected
		history.pushState(null, '', window.location.href)

		if (typeof window != 'undefined') {
			window.addEventListener('popstate', handlePopState)
		}

		if ($storeSearchedText && $storeSearchModal) {
			(async () => {
				await tick(); // Let modal render first
				const result = await executeSearch($storeSearchedText, { scope: scope })
				products = result?.products || []
				options = result?.options || {}
				sendWT3('search', { search_text: $storeSearchedText, products: products, options: options })
			})()
		}
	})

	onDestroy(() => {
		$storeActiveFilters = {}

		if (typeof window != 'undefined') {
			window.removeEventListener('popstate', handlePopState)
		}
	})

	beforeNavigate(({ to }) => {
		// Only close the modal if we're navigating away from a search URL
		// Don't close if we're navigating to a search URL (opening the modal)
		// Check if search parameter exists (even if empty) to determine if it's a search URL
		if (to?.url && !to.url.searchParams.has('search')) {
			$storeSearchModal = false
		}
	})

	function closeModal(e: any) {
		if (e?.target.id === 'searchModal' || e.key === 'Escape') {
			$storeSearchModal = false
			page.url.searchParams.delete('search')
			history.pushState({}, '', page.url.href)
		}
	}
</script>

<div
	id="searchModal"
	class="search-modal"
	onclick={(e) => closeModal(e)}
	onkeydown={(e) => closeModal(e)}
	role="button"
	tabindex="0"
>
	<div class="search-modal__limit">
		<div class="search-modal__inner">
			<Searchbar bind:options bind:products />

			<div class="search-modal__wrapper">
				{#if is_set_object($storeSearchFilters)
				&& (($storeSearchedText && $storeSearchedText.length > 2) || (products && products.length > 0))}
					<div class="search-modal__main">
						<div class="search-modal__filters" bind:this={productsDiv}>
							{#if products !== undefined}
								<Filters bind:products bind:options type="search" {endpoint} />
							{/if}
						</div>

						<div class="search-modal__products">
							<CategoryProducts bind:products bind:allResults type="search" />

							{#if is_set_array(products)}
								<IntersectionObserver
									element={elementOnce}
									rootMargin="500px"
									on:intersect={() => {
										(async () => {
											loadingProds = true
											start = start + 50
											options.start = start

											await getMoreSearchProducts(options, {scope: scope}).then((response) => {
												allResults = false

												if (response.length === 0) {
													allResults = true
												} else if (is_set_array(products) && is_set_array(response)) {
													products = products.concat(response)
												}
											})

											loadingProds = false
										})()
									}}
								>
									<div bind:this={elementOnce}></div>
								</IntersectionObserver>
							{/if}

							{#if loadingProds}
								<LoadingIcon type="search" />
							{/if}
						</div>
					</div>
				{:else}
					<div class="search-modal__main">
						<RecommendedProds />
					</div>
				{/if}

				{#if is_set_array(products)}
					<button aria-label="Scroll to top" class="to-top" onclick={() => productsDiv.scrollIntoView({ behavior: 'smooth' })}>
						<span class="icon icon-arr-right"></span>
					</button>
				{/if}
			</div>
		</div>
	</div>
</div>

<style lang="scss">
  .search-modal {
		z-index: 9;
		position: fixed;
		top: 0;
		bottom: 0;
		left: 0;
		right: 0;
		background: rgba(0, 0, 0, 0.5);
		transition: all linear 0.125s;

    &__limit {
      max-width: 2200px;
      margin: 0 auto;
    }

    &__inner {
      height: auto;
      max-height: calc(100vh - 30px);
      background: white;
      padding: 15px 0;
      transition: all linear 0.2s;

      @media (min-width: 1025px) {
        padding: 25px;
        max-height: calc(100vh - 50px);
      }

      @media (min-width: 1500px) {
        padding: 25px 80px;
      }
    }

    &__wrapper {
      padding-top: 10px;
      overflow-y: scroll;
      -ms-overflow-style: none;
      scrollbar-width: none;
      max-height: calc(100vh - 124px);

      @media (min-width: 1025px) {
        margin: 0 -30px;
        padding: 20px 30px;
        max-height: calc(100vh - 160px);
      }
    }

    .to-top {
      position: fixed;
      top: calc(100vh - 80px);
      right: 20px;
      width: 40px;
      height: 40px;
      background: white;
      box-shadow: 0px 0px 20px 10px rgba(0, 0, 0, 0.12);
      border-radius: 5px;

      @media (max-width: 1024px) {
        display: none;
      }

      span {
        display: block;
        transform: rotate(-90deg);
        color: var(--main);
        font-size: 12px;
      }
    }

    &__main {
      @media (min-width: 1025px) {
        display: flex;
        gap: 30px;
      }
    }

    &__products {
      padding: 20px 15px;
      flex-grow: 1;
    }

    &__filters {
      @media (max-width: 1024px) {
        position: fixed;
        bottom: 0;
        left: calc(50vw - 70px);
        z-index: 9;
      }
    }
  }
</style>
